/* eslint-disable max-params -- these linting errors come up constantly with reducers */
import type { PayloadAction } from "@reduxjs/toolkit";
import { createSlice } from "@reduxjs/toolkit";

import { LogoutReason } from "@/constants/Login";
import { useAppDispatch, useAppSelector } from "@/store/hooks";
import { extraReducersFactory } from "@/store/reducers/reset";

type AuthSliceState = {
  canPostLoginRoutingProceed: boolean;
  logoutReason: LogoutReason;
  shouldPreventApiCallsDueToExpiredToken: boolean;
};

const initialState: AuthSliceState = {
  // by default, post login routing can proceed; there are exceptional edge cases involving Android
  // where we want to route to the post login page, but not proceed just yet, pending biometric auth succeeding/failing
  canPostLoginRoutingProceed: true,
  logoutReason: LogoutReason.JustPreLoginCleanup,
  shouldPreventApiCallsDueToExpiredToken: false,
};

const authSlice = createSlice({
  // NOTE: extraReducers is required for resetAllReduxSlices to work as expected
  extraReducers: extraReducersFactory(initialState),
  initialState,
  name: "auth",
  reducers: {
    setCanPostLoginRoutingProceed: (state, action: PayloadAction<boolean>) => {
      return {
        ...state,
        canPostLoginRoutingProceed: action.payload,
      };
    },
    setLogoutReason: (state, action: PayloadAction<LogoutReason>) => {
      return {
        ...state,
        logoutReason: action.payload,
      };
    },
    setShouldPreventApiCallsDueToExpiredToken: (
      state,
      action: PayloadAction<boolean>,
    ) => {
      return {
        ...state,
        shouldPreventApiCallsDueToExpiredToken: action.payload,
      };
    },
  },
});

const {
  setCanPostLoginRoutingProceed: setCanPostLoginRoutingProceedAction,
  setLogoutReason: setLogoutReasonAction,
  setShouldPreventApiCallsDueToExpiredToken:
    setShouldPreventApiCallsDueToExpiredTokenAction,
} = authSlice.actions;

const useShouldPreventApiCallsDueToExpiredTokenSelector = () => {
  return useAppSelector(
    (state) => state.auth.shouldPreventApiCallsDueToExpiredToken,
  );
};

const useCanPostLoginRoutingProceedSelector = () => {
  return useAppSelector((state) => state.auth.canPostLoginRoutingProceed);
};

const useLogoutReasonSelector = () => {
  return useAppSelector((state) => state.auth.logoutReason);
};

const useAuthDispatch = () => {
  const dispatch = useAppDispatch();

  const setShouldPreventApiCallsDueToExpiredToken = (
    shouldPreventApiCallsDueToExpiredToken: boolean,
  ) => {
    dispatch(
      setShouldPreventApiCallsDueToExpiredTokenAction(
        shouldPreventApiCallsDueToExpiredToken,
      ),
    );
  };

  const setCanPostLoginRoutingProceed = (
    canPostLoginRoutingProceed: boolean,
  ) => {
    dispatch(setCanPostLoginRoutingProceedAction(canPostLoginRoutingProceed));
  };

  const setLogoutReason = (logoutReason: LogoutReason) => {
    dispatch(setLogoutReasonAction(logoutReason));
  };

  return {
    setCanPostLoginRoutingProceed,
    setLogoutReason,
    setShouldPreventApiCallsDueToExpiredToken,
  };
};

export {
  setCanPostLoginRoutingProceedAction,
  setLogoutReasonAction,
  setShouldPreventApiCallsDueToExpiredTokenAction,
  useAuthDispatch,
  useCanPostLoginRoutingProceedSelector,
  useLogoutReasonSelector,
  useShouldPreventApiCallsDueToExpiredTokenSelector,
};
export type { AuthSliceState };
export default authSlice.reducer;
